import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button, Typogaphy, Chip } from 'vnetwork-ui'
import FileIcons from '@/icons/FileIcons'
import SendMailInterviewDialog from '../../SendMailInterview'
import { CV_SCREEN_RESULTS, FINAL_STATUS } from '../../../../constants'
import { DEPARTMENT_SCOPES } from '@/constants/departments'
import { Cv } from '../../../../types'
import GokuDataGrid from '@/components/GokuDataGrid'
import { getCVMailerTrackings } from '../../../../services'
import CVAnalysisDisplay from '../cv-analysis-display'
import { useCVAnalyze } from '../use-cv-analyze'
import { finalStatusColor, screenResultColor } from '../cv-status-colors'
import { useCVTrackingColumns } from '../use-cv-tracking-columns'
import { availableFinalStatusButtons } from '../cv-final-status-options'
import toast from 'react-hot-toast'
import AsyncSelect from '@/components/Form/Shared/AsyncSelect'
import { getAllJobPublic } from '@/modules/Jobs/services'
import { useForm } from 'react-hook-form'
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from '@/components/ui/accordion'

interface ResultCVInformationProps {
    cv: any
    handleCvUrlClick: (
        url: string,
        isFile: boolean,
        cvName?: string
    ) => Promise<void>
    isGettingPresignedUrl: boolean
    matchedScreenResult: any
    matchedStatus: any
    mailTemplate: any
    setConfirmation: any
    handleSendMail: () => Promise<void>
    setShowDialogSendMailInterview: any
    showDialogSendMailInterview: {
        show: boolean
        data: Record<string, any> | null
    }
    refetch: any
    updateCVMutation: (values: Cv) => Promise<any>
}

const ResultCVInformation: React.FC<ResultCVInformationProps> = ({
    cv,
    handleCvUrlClick,
    isGettingPresignedUrl,
    matchedScreenResult,
    matchedStatus,
    mailTemplate,
    setConfirmation,
    handleSendMail,
    setShowDialogSendMailInterview,
    showDialogSendMailInterview,
    refetch,
    updateCVMutation,
}) => {
    const { t } = useTranslation()
    const { t: tResume } = useTranslation('resume')

    const [jdText, setJdText] = useState('')
    const [showEvaluateForm, setShowEvaluateForm] = useState(false)

    // Form for job select in evaluate
    const { control: evalControl, watch: evalWatch } = useForm<{
        job_id: number
    }>({
        defaultValues: { job_id: 0 },
    })
    const selectedJobId = evalWatch('job_id')

    // Fetch job options with job_id as value
    const getJobOptionsForEvaluate = async () => {
        const response = await getAllJobPublic()
        if (!response?.results?.length) return []
        return response.results.map((item) => ({
            label: item?.title,
            value: item?.job_id,
        }))
    }

    const { isEvaluating, analysisResult, buildProfile, evaluateAgainstJD } =
        useCVAnalyze({
            cvID: cv?.result?.id,
            cvUrl: cv?.result?.url,
            cachedAnalysis: cv?.result?.cv_analysis,
            onSuccess: refetch,
        })

    const handleBuildProfile = buildProfile

    // handleAnalyzeCV picks JD from job_id select or raw text and runs evaluation.
    const handleAnalyzeCV = () => {
        const payload: { job_id?: number; jd?: string } = {}
        if (selectedJobId) payload.job_id = selectedJobId
        else if (jdText.trim()) payload.jd = jdText.trim()
        return evaluateAgainstJD(payload)
    }
    const [noteValues, setNoteValues] = useState({
        interview_note: cv?.result?.interview_note || '',
        hr_comment: cv?.result?.hr_comment || '',
        leader_comment: cv?.result?.leader_comment || '',
    })

    // Đếm số lần đã gửi theo template
    const mailSendCounts = useMemo(() => {
        const counts: Record<string, number> = {}
        ;(cv?.result?.mail_send || []).forEach((tpl: string) => {
            counts[tpl] = (counts[tpl] || 0) + 1
        })
        return counts
    }, [cv?.result?.mail_send])

    const formatSendLabel = (templateKey: string, baseLabel: string) => {
        const count = mailSendCounts[templateKey] || 0
        return count > 0 ? `${baseLabel} (đã gửi ${count} lần)` : baseLabel
    }

    const finalStatusButtons = useMemo(
        () => availableFinalStatusButtons(cv?.result?.final_status),
        [cv?.result?.final_status]
    )

    const handleSaveNote = async (
        field: 'interview_note' | 'hr_comment' | 'leader_comment',
        value: string
    ) => {
        setNoteValues((prev) => ({ ...prev, [field]: value }))
        if (!cv?.result) return
        await updateCVMutation({
            ...(cv.result as Cv),
            [field]: value,
        } as Cv)
    }

    const trackingColumns = useCVTrackingColumns()

    const handleUpdateScreenResult = (result: string) => {
        const resultLabel =
            CV_SCREEN_RESULTS.find((r) => r.value === result)?.label || result
        setConfirmation({
            open: true,
            title: tResume('confirm-update-cv-screen-result'),
            description: tResume(
                'confirm-update-cv-screen-result-description',
                {
                    result: resultLabel,
                }
            ),
            onConfirm: async () => {
                if (!cv?.result) return
                await updateCVMutation({
                    ...(cv.result as Cv),
                    cover_letter_screen_result: result,
                })
            },
        })
    }

    const handleUpdateFinalStatus = (status: string) => {
        const statusLabel =
            FINAL_STATUS.find((s) => s.value === status)?.label || status
        setConfirmation({
            open: true,
            title: tResume('confirm-update-cv-screen-result'),
            description: tResume(
                'confirm-update-cv-screen-result-description',
                {
                    result: statusLabel,
                }
            ),
            onConfirm: async () => {
                if (!cv?.result) return
                await updateCVMutation({
                    ...(cv.result as Cv),
                    final_status: status,
                })
            },
        })
    }

    return (
        <>
            <div className="bg-white rounded-md border border-gray-200 shadow-sm hover:shadow-md transition-shadow duration-300 overflow-hidden">
                <div className="bg-gradient-to-r from-emerald-50 to-teal-50 px-3 py-2 border-b border-gray-200">
                    <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shadow-md">
                            <FileIcons className="w-4 h-4 text-white" />
                        </div>
                        <Typogaphy className="font-medium text-base text-gray-800">
                            {tResume('result-cv-information')}
                        </Typogaphy>
                    </div>
                </div>
                <div className="p-3 space-y-1">
                    {cv?.result?.url && (
                        <div className="grid grid-cols-3 gap-2 py-1.5 px-2 hover:bg-gray-50 transition-colors border-b border-gray-100">
                            <Typogaphy className="font-medium text-sm text-gray-600">
                                {t('url-cv')}:
                            </Typogaphy>
                            <div className="col-span-2">
                                <Button
                                    onClick={() =>
                                        handleCvUrlClick(
                                            cv?.result?.url,
                                            cv?.result?.type_file === 'file',
                                            cv?.result?.name
                                        )
                                    }
                                    disabled={isGettingPresignedUrl}
                                    variant="contained"
                                    size="small"
                                    className="min-w-[180px] text-xs"
                                >
                                    {isGettingPresignedUrl
                                        ? t('loading...')
                                        : t('preview')}
                                </Button>
                            </div>
                        </div>
                    )}
                    {!cv?.result?.cover_letter_screen_result ? (
                        <div className="grid grid-cols-3 gap-2 py-1.5 px-2 hover:bg-gray-50 transition-colors border-b border-gray-100">
                            <Typogaphy className="font-medium text-sm text-gray-600">
                                {t('cv-screen-results')}:
                            </Typogaphy>
                            <div className="col-span-2 space-y-2">
                                <div className="flex flex-wrap gap-2">
                                    {cv?.result?.url && (
                                        <>
                                            <Button
                                                variant="contained"
                                                size="small"
                                                color="primary"
                                                onClick={handleBuildProfile}
                                                loading={isEvaluating}
                                                disabled={isEvaluating}
                                                className="min-w-[180px] text-xs"
                                            >
                                                {tResume('build-ai-profile')}
                                            </Button>
                                            <Button
                                                variant="contained"
                                                size="small"
                                                color="info"
                                                onClick={() =>
                                                    setShowEvaluateForm(
                                                        !showEvaluateForm
                                                    )
                                                }
                                                className="min-w-[180px] text-xs"
                                            >
                                                {tResume('evaluate-cv')}
                                            </Button>
                                        </>
                                    )}
                                    {CV_SCREEN_RESULTS?.map((result) => (
                                        <Button
                                            key={result.value}
                                            variant="outlined"
                                            size="small"
                                            color={screenResultColor(
                                                result.value
                                            )}
                                            onClick={() =>
                                                handleUpdateScreenResult(
                                                    result.value
                                                )
                                            }
                                            className="min-w-[180px] text-xs"
                                        >
                                            {result.label}
                                        </Button>
                                    ))}
                                </div>
                                {/* AI Evaluate form: job select + JD text input */}
                                {showEvaluateForm && (
                                    <div className="bg-blue-50 p-3 rounded-md border border-blue-200 space-y-2">
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                            <div>
                                                <AsyncSelect
                                                    asyncOptions={
                                                        getJobOptionsForEvaluate
                                                    }
                                                    name="job_id"
                                                    control={evalControl}
                                                    label={tResume(
                                                        'evaluate-select-job'
                                                    )}
                                                    placeholder={tResume(
                                                        'evaluate-select-job'
                                                    )}
                                                />
                                            </div>
                                            <div>
                                                <Typogaphy className="text-xs text-gray-500 mb-1">
                                                    {tResume(
                                                        'evaluate-or-input-jd'
                                                    )}
                                                </Typogaphy>
                                                <textarea
                                                    className="w-full text-sm bg-white p-2 rounded-md border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-400"
                                                    rows={3}
                                                    placeholder={tResume(
                                                        'evaluate-jd-placeholder'
                                                    )}
                                                    value={jdText}
                                                    onChange={(e) =>
                                                        setJdText(
                                                            e.target.value
                                                        )
                                                    }
                                                    disabled={!!selectedJobId}
                                                />
                                            </div>
                                        </div>
                                        <Button
                                            variant="contained"
                                            size="small"
                                            color="info"
                                            onClick={handleAnalyzeCV}
                                            loading={isEvaluating}
                                            disabled={isEvaluating}
                                            className="text-xs"
                                        >
                                            {isEvaluating
                                                ? tResume('evaluating-cv')
                                                : tResume('evaluate-cv')}
                                        </Button>
                                    </div>
                                )}
                            </div>
                        </div>
                    ) : (
                        matchedScreenResult?.label && (
                            <div className="grid grid-cols-3 gap-2 py-1.5 px-2 hover:bg-gray-50 transition-colors border-b border-gray-100">
                                <Typogaphy className="font-medium text-sm text-gray-600">
                                    {t('cv-screen-results')}:
                                </Typogaphy>
                                <div className="col-span-2 space-y-2">
                                    <div className="flex items-center gap-2">
                                        <Chip
                                            label={matchedScreenResult?.label}
                                            color={screenResultColor(
                                                matchedScreenResult?.value
                                            )}
                                            size="small"
                                            className="font-medium text-xs"
                                        />
                                        {cv?.result?.url && (
                                            <>
                                                <Button
                                                    variant="outlined"
                                                    size="small"
                                                    color="primary"
                                                    onClick={handleBuildProfile}
                                                    loading={isEvaluating}
                                                    disabled={isEvaluating}
                                                    className="text-xs h-6"
                                                >
                                                    {tResume(
                                                        'build-ai-profile'
                                                    )}
                                                </Button>
                                                <Button
                                                    variant="outlined"
                                                    size="small"
                                                    color="info"
                                                    onClick={() =>
                                                        setShowEvaluateForm(
                                                            !showEvaluateForm
                                                        )
                                                    }
                                                    className="text-xs h-6"
                                                >
                                                    {tResume('evaluate-cv')}
                                                </Button>
                                            </>
                                        )}
                                    </div>
                                    {/* Re-evaluate form */}
                                    {showEvaluateForm && (
                                        <div className="bg-blue-50 p-3 rounded-md border border-blue-200 space-y-2">
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                                <div>
                                                    <AsyncSelect
                                                        asyncOptions={
                                                            getJobOptionsForEvaluate
                                                        }
                                                        name="job_id"
                                                        control={evalControl}
                                                        label={tResume(
                                                            'evaluate-select-job'
                                                        )}
                                                        placeholder={tResume(
                                                            'evaluate-select-job'
                                                        )}
                                                    />
                                                </div>
                                                <div>
                                                    <Typogaphy className="text-xs text-gray-500 mb-1">
                                                        {tResume(
                                                            'evaluate-or-input-jd'
                                                        )}
                                                    </Typogaphy>
                                                    <textarea
                                                        className="w-full text-sm bg-white p-2 rounded-md border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-400"
                                                        rows={3}
                                                        placeholder={tResume(
                                                            'evaluate-jd-placeholder'
                                                        )}
                                                        value={jdText}
                                                        onChange={(e) =>
                                                            setJdText(
                                                                e.target.value
                                                            )
                                                        }
                                                        disabled={
                                                            !!selectedJobId
                                                        }
                                                    />
                                                </div>
                                            </div>
                                            <Button
                                                variant="contained"
                                                size="small"
                                                color="info"
                                                onClick={handleAnalyzeCV}
                                                loading={isEvaluating}
                                                disabled={isEvaluating}
                                                className="text-xs"
                                            >
                                                {isEvaluating
                                                    ? tResume('evaluating-cv')
                                                    : tResume('evaluate-cv')}
                                            </Button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )
                    )}
                    {analysisResult && (
                        <CVAnalysisDisplay analysis={analysisResult} />
                    )}
                    {/* Hiển thị và cập nhật final_status khi PASS */}
                    {matchedScreenResult?.value === 'PASS' && (
                        <div className="grid grid-cols-3 gap-2 py-1.5 px-2 hover:bg-gray-50 transition-colors border-b border-gray-100">
                            <Typogaphy className="font-medium text-sm text-gray-600">
                                {t('cv-final-status')}:
                            </Typogaphy>
                            <div className="col-span-2 flex flex-col gap-2">
                                <div className="flex flex-wrap gap-2 items-center">
                                    <Chip
                                        label={
                                            matchedStatus?.label ||
                                            cv?.result?.final_status ||
                                            '-'
                                        }
                                        color={finalStatusColor(
                                            cv?.result?.final_status
                                        )}
                                        size="small"
                                        className="font-medium text-xs"
                                    />
                                </div>
                                {finalStatusButtons.length > 0 && (
                                    <div className="flex flex-wrap gap-2 items-center">
                                        {finalStatusButtons.map((status) => (
                                            <Button
                                                key={status.value}
                                                variant="outlined"
                                                size="small"
                                                onClick={() =>
                                                    handleUpdateFinalStatus(
                                                        status.value
                                                    )
                                                }
                                                className="text-xs"
                                            >
                                                {status.label}
                                            </Button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                    {/* Nhận xét dùng textarea auto-resize, lưu onBlur */}
                    {(
                        [
                            ['interview_note', t('cv-interview-note')],
                            ['hr_comment', t('cv-hr-comment')],
                            ['leader_comment', t('cv-leader-comment')],
                        ] as const
                    ).map(([field, label]) => (
                        <div
                            key={field}
                            className="grid grid-cols-3 gap-2 py-1.5 px-2 hover:bg-gray-50 transition-colors border-b border-gray-100"
                        >
                            <Typogaphy className="font-medium text-sm text-gray-600">
                                {label}:
                            </Typogaphy>
                            <div className="col-span-2">
                                <textarea
                                    className="w-full text-sm text-gray-800 whitespace-pre-wrap leading-relaxed bg-gray-50 p-2 rounded-md border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary resize-none overflow-hidden"
                                    style={{ minHeight: '2.5rem' }}
                                    value={noteValues[field]}
                                    onChange={(e) => {
                                        setNoteValues((prev) => ({
                                            ...prev,
                                            [field]: e.target.value,
                                        }))
                                        // Auto-resize
                                        e.target.style.height = 'auto'
                                        e.target.style.height = `${e.target.scrollHeight}px`
                                    }}
                                    onBlur={(e) =>
                                        handleSaveNote(field, e.target.value)
                                    }
                                    ref={(el) => {
                                        // Auto-resize on mount
                                        if (el) {
                                            el.style.height = 'auto'
                                            el.style.height = `${el.scrollHeight}px`
                                        }
                                    }}
                                />
                            </div>
                        </div>
                    ))}
                    {/* Action buttons — separated bar */}
                    {(matchedScreenResult?.value === 'PASS' ||
                        matchedScreenResult?.mail_template ||
                        matchedStatus?.mail_template) && (
                        <div className="flex flex-wrap gap-3 mt-4 mx-2 p-3 bg-gray-50 rounded-md border border-gray-200">
                            {/* Send Mail Interview khi PASS */}
                            {matchedScreenResult?.value === 'PASS' && (
                                <Button
                                    variant="outlined"
                                    onClick={() => {
                                        setShowDialogSendMailInterview({
                                            show: true,
                                            data: cv?.result || {},
                                        })
                                    }}
                                    disabled={
                                        cv?.result?.final_status != '' &&
                                        cv?.result?.final_status !== 'Interview'
                                    }
                                >
                                    {formatSendLabel(
                                        'send_mail_interview',
                                        tResume('send-mail-interview')
                                    )}
                                </Button>
                            )}
                            {/* Send Mail Thank You Talent khi PASS và final_status là Fail Interview */}
                            {matchedScreenResult?.value === 'PASS' &&
                                matchedStatus?.mail_template ===
                                    'thank_talent' && (
                                    <Button
                                        variant="outlined"
                                        onClick={() => {
                                            setConfirmation({
                                                open: true,
                                                title: t('send-mail-title-cv'),
                                                description: `Template Email: ${matchedStatus.mail_template}. ${t(
                                                    'send-mail-description-cv'
                                                )}`,
                                                onConfirm: async () => {
                                                    await handleSendMail()
                                                },
                                            })
                                        }}
                                    >
                                        {formatSendLabel(
                                            'thank_talent',
                                            t('send-mail-thank-you-talent')
                                        )}
                                    </Button>
                                )}
                            {/* Send Mail khi FAIL hoặc CLOSE JOB */}
                            {matchedScreenResult?.mail_template &&
                                matchedScreenResult?.value !== 'PASS' && (
                                    <Button
                                        onClick={() =>
                                            setConfirmation({
                                                open: true,
                                                title: t('send-mail-title-cv'),
                                                description: `Template Email: ${matchedScreenResult.mail_template}. ${t(
                                                    'send-mail-description-cv'
                                                )}`,
                                                onConfirm: async () => {
                                                    await handleSendMail()
                                                },
                                            })
                                        }
                                        type="button"
                                        variant="outlined"
                                    >
                                        {formatSendLabel(
                                            matchedScreenResult.mail_template,
                                            t('send-mail-thank-you-letter')
                                        )}
                                    </Button>
                                )}
                            {/* Send Mail cho final_status khác (không phải send_mail_interview và thank_talent) */}
                            {matchedStatus?.mail_template &&
                                matchedStatus?.mail_template !==
                                    'send_mail_interview' &&
                                matchedStatus?.mail_template !==
                                    'thank_talent' && (
                                    <Button
                                        variant="outlined"
                                        onClick={() => {
                                            setConfirmation({
                                                open: true,
                                                title: t('send-mail-title-cv'),
                                                description: `Template Email: ${mailTemplate}. ${t(
                                                    'send-mail-description-cv'
                                                )}`,
                                                onConfirm: async () => {
                                                    await handleSendMail()
                                                },
                                            })
                                        }}
                                    >
                                        {formatSendLabel(
                                            matchedStatus.mail_template,
                                            t('send-mail')
                                        )}
                                    </Button>
                                )}
                            {/* Tạo tài khoản khi Tuyển dụng */}
                            {matchedScreenResult?.value === 'PASS' &&
                                cv?.result?.final_status === 'Tuyển dụng' && (
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        onClick={() => {
                                            const name = cv?.result?.name || ''
                                            const email =
                                                cv?.result?.email || ''
                                            const department =
                                                cv?.result?.department || ''
                                            const positionType =
                                                cv?.result?.type_of_position ||
                                                ''

                                            // Map type_of_position -> employee_type
                                            const positionToEmployeeType: Record<
                                                string,
                                                string
                                            > = {
                                                Intern: 'intern',
                                                Trainee: 'probation',
                                                Staff: 'official',
                                                'Team Lead': 'official',
                                                Manager: 'official',
                                                Head: 'board_of_directors',
                                            }
                                            const mappedEmployeeType =
                                                positionToEmployeeType[
                                                    positionType
                                                ] ||
                                                cv?.result?.employee_type ||
                                                ''

                                            // Map department_name -> department_id via DEPARTMENT_SCOPES
                                            const deptMap: Record<
                                                string,
                                                string
                                            > = {
                                                MKT: 'MARKETING',
                                                CS: 'SALES',
                                                SALES: 'SALES',
                                                TECH: 'TECHNOLOGY',
                                                DEV: 'DEVELOPMENT',
                                                FINANCE: 'FINANCE',
                                                HR: 'HUMAN RESOURCE',
                                                'B.O.D': 'BOARD OF DIRECTORS',
                                            }
                                            const scopeName =
                                                deptMap[department] ||
                                                department
                                            const matchedDept =
                                                DEPARTMENT_SCOPES.find(
                                                    (d) => d.name === scopeName
                                                )
                                            const departmentId =
                                                matchedDept?.department_id

                                            const url = `/employees/new?name=${encodeURIComponent(
                                                name
                                            )}&email=${encodeURIComponent(
                                                email
                                            )}${
                                                departmentId
                                                    ? `&department_id=${departmentId}`
                                                    : ''
                                            }&department_name=${encodeURIComponent(
                                                department
                                            )}&employee_type=${encodeURIComponent(
                                                mappedEmployeeType
                                            )}`
                                            window.open(url, '_blank')
                                        }}
                                    >
                                        {t('create-employee-account') ||
                                            'Tạo tài khoản nhân viên'}
                                    </Button>
                                )}
                        </div>
                    )}
                </div>
            </div>

            {showDialogSendMailInterview.show && (
                <SendMailInterviewDialog
                    open={true}
                    onClose={() =>
                        setShowDialogSendMailInterview({
                            show: false,
                            data: null,
                        })
                    }
                    updateData={showDialogSendMailInterview.data || {}}
                    refetch={refetch}
                />
            )}

            {/* Email Tracking Section — collapsed by default */}
            {cv?.result?.id && (
                <div className="bg-white rounded-md border border-gray-200 shadow-sm hover:shadow-md transition-shadow duration-300 overflow-hidden mt-2">
                    <Accordion type="single" collapsible>
                        <AccordionItem
                            value="email-tracking"
                            className="border-b-0"
                        >
                            <AccordionTrigger className="px-3 py-2 bg-gradient-to-r from-blue-50 to-indigo-50 hover:no-underline border-b border-gray-200">
                                <Typogaphy className="font-medium text-base text-gray-800">
                                    {tResume('email-tracking')}
                                </Typogaphy>
                            </AccordionTrigger>
                            <AccordionContent>
                                <div className="p-3">
                                    <GokuDataGrid
                                        apiCall={(payload) =>
                                            getCVMailerTrackings({
                                                ...payload,
                                                cv_id: cv?.result?.id,
                                            })
                                        }
                                        columns={trackingColumns}
                                        dataGridProps={{
                                            getRowId: (row) =>
                                                String(
                                                    row?.mailer_id ||
                                                        row?.mailer_tracking_id
                                                ),
                                        }}
                                        reactQueryKey={[
                                            'cv-mailer-trackings',
                                            cv?.result?.id,
                                        ].join('.')}
                                        options={{
                                            title: undefined,
                                            pageSize: 5,
                                            showSearch: false,
                                        }}
                                        paginationMode="server"
                                    />
                                </div>
                            </AccordionContent>
                        </AccordionItem>
                    </Accordion>
                </div>
            )}
        </>
    )
}

export default ResultCVInformation
